/**
 * @fileoverview Manual InfoTrack order form — picks a wired product and
 * collects the inputs required for that product's API endpoint.
 *
 * Only `wired` products from the registry are listed (the catalog query
 * filters them server-side).
 */

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { useConvex } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import { Label } from "@/ui/primitives/label";
import { Input } from "@/ui/primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { Checkbox } from "@/ui/primitives/checkbox";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface AsicPersonalNameCandidate {
  nameIdentifier: string;
  displayLabel: string;
  givenNames?: string;
  familyName?: string;
  dateOfBirth?: string;
  availableExtractTypes: string[];
}

/**
 * Prefill payload accepted by `SearchesManualTrigger`. Lets the recommended
 * list jump the practitioner straight into the right form pre-populated
 * with the seeded inputs — used by the ASIC personal extract "Configure"
 * flow because that product needs a synchronous lookup before a real
 * order can be approved.
 *
 * `nonce` is bumped every time we want to apply a fresh prefill (even if
 * the productKey/inputs are identical to last time) so React's effect
 * fires reliably.
 */
export interface ManualTriggerPrefill {
  nonce: number;
  productKey: string;
  asicPersonalExtract?: {
    familyName: string;
    givenNames: string;
    extractType?: "Current" | "Historical";
  };
  recommendationId?: Id<"infoTrackRecommendations">;
}

const AU_STATES: ReadonlyArray<{ value: string; label: string }> = [
  { value: "NSW", label: "NSW" },
  { value: "VIC", label: "VIC" },
  { value: "QLD", label: "QLD" },
  { value: "SA", label: "SA" },
  { value: "WA", label: "WA" },
  { value: "TAS", label: "TAS" },
  { value: "NT", label: "NT" },
  { value: "ACT", label: "ACT" },
];

const TITLE_PRODUCTS = new Set([
  "land_title_search",
  "land_title_by_address",
  "land_title_by_owner_name",
]);

export function SearchesManualTrigger({
  jobId,
  prefill,
  onPrefillConsumed,
}: {
  jobId: Id<"jobs">;
  /**
   * Optional payload pushed in from the Recommended list (or any other
   * caller) to pre-populate the form. The component reads this via an
   * effect keyed on `prefill.nonce` and then invokes `onPrefillConsumed`
   * to let the caller clear the slot.
   */
  prefill?: ManualTriggerPrefill;
  onPrefillConsumed?: () => void;
}) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const { data: catalog } = useQuery({
    ...convexQuery(api.jobs.searches.queries.listInfotrackProductCatalog, {}),
  });

  const { data: job } = useQuery({
    ...convexQuery(api.jobs.queries.getJob, { jobId }),
    enabled: !!jobId,
  });

  const submitManual = useConvexMutation(
    api.jobs.searches.mutations.submitManualInfotrackSearch,
  );

  const convex = useConvex();
  const lookupAsicPersonalName = useMutation({
    mutationFn: async () =>
      convex.action(api.jobs.searches.actions.lookupAsicPersonalName, {
        jobId,
        familyName: asicPersFamily.trim(),
        givenNames: asicPersGiven.trim(),
        dateOfBirth: asicPersDob ? asicPersDob : undefined,
      }),
    onSuccess: (result) => {
      setAsicPersParentOrderId(result.parentOrderId);
      setAsicPersCandidates(result.candidates);
      setAsicPersSelectedIds(new Set());
      if (result.candidates.length === 0) {
        toast.info(
          result.errorMessages.length > 0
            ? `No matches: ${result.errorMessages.join("; ")}`
            : "No ASIC records matched that name.",
        );
      } else {
        toast.success(
          `Found ${result.candidates.length} match${result.candidates.length === 1 ? "" : "es"} — pick which to extract.`,
        );
      }
    },
    onError: (e: Error) => toast.error(e.message || "ASIC lookup failed."),
  });

  const [registry, setRegistry] = useState<"" | "asic" | "ppsr" | "lto">("");
  const [productKey, setProductKey] = useState<string>("");

  // Title-by-reference / by-address / by-owner all share the same state field.
  const [titleState, setTitleState] = useState("NSW");

  // Title-by-reference
  const [titleReference, setTitleReference] = useState("");

  // Title-by-address
  const [streetNumber, setStreetNumber] = useState("");
  const [streetName, setStreetName] = useState("");
  const [streetType, setStreetType] = useState("");
  const [suburb, setSuburb] = useState("");
  const [postCode, setPostCode] = useState("");

  // Title-by-owner-name
  const [nameType, setNameType] = useState<"Person" | "Company">("Person");
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");

  // ASIC company extract
  const [acn, setAcn] = useState("");
  const [asicInfoType, setAsicInfoType] = useState<
    "Current" | "Historical" | "Relational"
  >("Historical");
  const [asicOnFile, setAsicOnFile] = useState<
    "Disabled" | "Preferred" | "Required"
  >("Disabled");

  // PPSR grantor search (organisation): identify by ACN/ABN/etc OR by name
  const [ppsrOrgIdMode, setPpsrOrgIdMode] = useState<"number" | "name">(
    "number",
  );
  const [ppsrOrgNumber, setPpsrOrgNumber] = useState("");
  const [ppsrOrgNumberType, setPpsrOrgNumberType] = useState<
    "Acn" | "Abn" | "Arbn" | "Arsn"
  >("Acn");
  const [ppsrOrgName, setPpsrOrgName] = useState("");

  // PPSR grantor search (individual)
  const [ppsrGivenNames, setPpsrGivenNames] = useState("");
  const [ppsrFamilyName, setPpsrFamilyName] = useState("");
  const [ppsrDob, setPpsrDob] = useState("");

  // PPSR grantor searches (both): which report PDF to generate. Detailed is
  // the default — the Summary omits the secured party group's ACN and
  // address for service.
  const [ppsrReportType, setPpsrReportType] = useState<"Detailed" | "Summary">(
    "Detailed",
  );

  // ASIC personal extract — two-step flow
  const [asicPersFamily, setAsicPersFamily] = useState("");
  const [asicPersGiven, setAsicPersGiven] = useState("");
  const [asicPersDob, setAsicPersDob] = useState("");
  const [asicPersExtractType, setAsicPersExtractType] = useState<
    "Current" | "Historical"
  >("Historical");
  const [asicPersParentOrderId, setAsicPersParentOrderId] = useState<
    number | null
  >(null);
  const [asicPersCandidates, setAsicPersCandidates] = useState<
    AsicPersonalNameCandidate[]
  >([]);
  const [asicPersSelectedIds, setAsicPersSelectedIds] = useState<Set<string>>(
    new Set(),
  );

  // PPSR collateral report — picker for completed parent grantor searches
  const [collateralSelectedIds, setCollateralSelectedIds] = useState<
    Set<Id<"infoTrackOrders">>
  >(new Set());

  const collateralCandidatesQuery = useQuery({
    ...convexQuery(
      api.jobs.searches.queries.listPpsrGrantorOrdersForCollateralPicker,
      { jobId },
    ),
    enabled: productKey === "ppsr_company_collateral_report",
  });

  const isReady = (): boolean => {
    if (!productKey || commitMutation.isPending) return false;
    switch (productKey) {
      case "land_title_search":
        return !!titleReference.trim();
      case "land_title_by_address":
        return !!(streetName.trim() || suburb.trim());
      case "land_title_by_owner_name":
        return nameType === "Company"
          ? !!companyName.trim()
          : !!lastName.trim();
      case "asic_company_extract_current_historical":
        return !!acn.trim();
      case "ppsr_grantor_search":
        return ppsrOrgIdMode === "number"
          ? !!ppsrOrgNumber.trim()
          : !!(ppsrOrgName.trim() || job?.entityName);
      case "ppsr_grantor_individual_search":
        return (
          !!ppsrGivenNames.trim() &&
          !!ppsrFamilyName.trim() &&
          /^\d{4}-\d{2}-\d{2}$/.test(ppsrDob)
        );
      case "asic_personal_extract_current_historical":
        return asicPersParentOrderId !== null && asicPersSelectedIds.size > 0;
      case "ppsr_company_collateral_report":
        return collateralSelectedIds.size > 0;
      default:
        return true;
    }
  };

  const buildPayload = (): Record<string, unknown> => {
    switch (productKey) {
      case "land_title_search":
        return {
          state: titleState,
          titleReference: titleReference.trim(),
        };
      case "land_title_by_address":
        return {
          state: titleState,
          streetNumberFrom: streetNumber.trim() || undefined,
          streetName: streetName.trim() || undefined,
          streetType: streetType.trim() || undefined,
          suburb: suburb.trim() || undefined,
          postCode: postCode.trim() || undefined,
        };
      case "land_title_by_owner_name":
        return nameType === "Company"
          ? {
              state: titleState,
              nameType: "Company",
              companyName: companyName.trim(),
            }
          : {
              state: titleState,
              nameType: "Person",
              firstName: firstName.trim() || undefined,
              middleName: middleName.trim() || undefined,
              lastName: lastName.trim(),
            };
      case "asic_company_extract_current_historical":
        return {
          organisationNumber: acn.trim(),
          informationType: asicInfoType,
          onFileOption: asicOnFile,
        };
      case "ppsr_grantor_search":
        return ppsrOrgIdMode === "number"
          ? {
              organisationNumber: ppsrOrgNumber.trim(),
              organisationNumberType: ppsrOrgNumberType,
              reportType: ppsrReportType,
            }
          : {
              organisationName: ppsrOrgName.trim() || job?.entityName,
              reportType: ppsrReportType,
            };
      case "ppsr_grantor_individual_search":
        return {
          givenNames: ppsrGivenNames.trim(),
          familyName: ppsrFamilyName.trim(),
          dateOfBirth: ppsrDob,
          reportType: ppsrReportType,
        };
      case "asic_personal_extract_current_historical":
        return {
          parentOrderId: asicPersParentOrderId,
          nameIdentifiers: Array.from(asicPersSelectedIds),
          extractType: asicPersExtractType,
        };
      case "ppsr_company_collateral_report": {
        const candidates = collateralCandidatesQuery.data ?? [];
        const grantorSearchOrderIds = candidates
          .filter((c) => collateralSelectedIds.has(c._id))
          .map((c) => Number(c.remoteOrderId))
          .filter((n) => Number.isFinite(n) && n > 0);
        return { grantorSearchOrderIds };
      }
      default:
        return {};
    }
  };

  const commitMutation = useMutation({
    mutationFn: async () =>
      submitManual({ jobId, productKey, inputPayload: buildPayload() }),
    onSuccess: () => {
      toast.success("Manual search submitted");
      setTitleReference("");
      setStreetNumber("");
      setStreetName("");
      setStreetType("");
      setSuburb("");
      setPostCode("");
      setFirstName("");
      setMiddleName("");
      setLastName("");
      setCompanyName("");
      setPpsrOrgNumber("");
      setPpsrOrgName("");
      setPpsrGivenNames("");
      setPpsrFamilyName("");
      setPpsrDob("");
      setPpsrReportType("Detailed");
      setAsicPersFamily("");
      setAsicPersGiven("");
      setAsicPersDob("");
      setAsicPersParentOrderId(null);
      setAsicPersCandidates([]);
      setAsicPersSelectedIds(new Set());
      setCollateralSelectedIds(new Set());
      setRegistry("");
      setProductKey("");
    },
    onError: (e: Error) =>
      toast.error(e.message || "Could not submit manual search."),
  });

  // Apply incoming prefill (e.g. "Configure" click on a personal-extract
  // recommendation in the Recommended list). Keyed on `nonce` so repeat
  // prefills with the same payload still re-trigger the form switch.
  const lastAppliedNonceRef = useRef<number | null>(null);
  useEffect(() => {
    if (!prefill) return;
    if (lastAppliedNonceRef.current === prefill.nonce) return;
    if (!catalog) return;
    lastAppliedNonceRef.current = prefill.nonce;

    const product = catalog.find((p) => p.key === prefill.productKey);
    // KYC & AML products live on their own tab and never prefill this form —
    // only adopt registries this picker actually offers.
    if (
      product &&
      (product.registry === "asic" ||
        product.registry === "ppsr" ||
        product.registry === "lto")
    ) {
      setRegistry(product.registry);
    }
    setProductKey(prefill.productKey);
    if (prefill.asicPersonalExtract) {
      setAsicPersFamily(prefill.asicPersonalExtract.familyName);
      setAsicPersGiven(prefill.asicPersonalExtract.givenNames);
      setAsicPersDob("");
      setAsicPersExtractType(
        prefill.asicPersonalExtract.extractType ?? "Historical",
      );
      setAsicPersParentOrderId(null);
      setAsicPersCandidates([]);
      setAsicPersSelectedIds(new Set());
    }
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    onPrefillConsumed?.();
  }, [prefill, onPrefillConsumed, catalog]);

  if (!catalog?.length) {
    return null;
  }

  return (
    <div
      ref={cardRef}
      className="space-y-4 rounded-lg border bg-background p-6"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="manual-registry">Registry</Label>
          <Select
            value={registry}
            onValueChange={(v) => {
              const next = (
                v === "asic" || v === "ppsr" || v === "lto" ? v : ""
              ) as "" | "asic" | "ppsr" | "lto";
              setRegistry(next);
              // Switching registry resets the product so we don't end up
              // with a stale product selected from a different registry.
              setProductKey("");
            }}
          >
            <SelectTrigger id="manual-registry">
              <SelectValue placeholder="Choose registry" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="asic">ASIC</SelectItem>
              <SelectItem value="ppsr">PPSR</SelectItem>
              <SelectItem value="lto">Land titles</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="manual-product">Product</Label>
          <Select
            value={productKey}
            disabled={!registry}
            onValueChange={(v) => {
              setProductKey(v);
              setAcn(job?.acn?.trim() ?? "");
              setPpsrOrgNumber(job?.acn?.trim() ?? "");
              setPpsrOrgNumberType("Acn");
              setPpsrOrgIdMode(job?.acn ? "number" : "name");
              setPpsrOrgName(job?.entityName ?? "");
              setCompanyName(job?.entityName ?? "");
              // Reset two-step flows on product change so stale state
              // doesn't leak between products (e.g. ASIC parent orderId).
              setAsicPersParentOrderId(null);
              setAsicPersCandidates([]);
              setAsicPersSelectedIds(new Set());
              setCollateralSelectedIds(new Set());
            }}
          >
            <SelectTrigger id="manual-product">
              <SelectValue
                placeholder={
                  registry ? "Choose product" : "Pick a registry first"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {catalog
                .filter((p) => !registry || p.registry === registry)
                .map((p) => (
                  <SelectItem key={p.key} value={p.key}>
                    {p.productLabel}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {TITLE_PRODUCTS.has(productKey) ? (
        <div className="space-y-2">
          <Label htmlFor="title-state">State</Label>
          <Select value={titleState} onValueChange={setTitleState}>
            <SelectTrigger id="title-state">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AU_STATES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {productKey === "land_title_search" ? (
        <div className="space-y-2">
          <Label htmlFor="title-ref">Title reference</Label>
          <Input
            id="title-ref"
            value={titleReference}
            onChange={(e) => setTitleReference(e.target.value)}
            placeholder="e.g. 1863/1000001"
          />
        </div>
      ) : null}

      {productKey === "land_title_by_address" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-1">
            <Label htmlFor="addr-num">Street number</Label>
            <Input
              id="addr-num"
              value={streetNumber}
              onChange={(e) => setStreetNumber(e.target.value)}
              placeholder="e.g. 12"
            />
          </div>
          <div className="space-y-2 sm:col-span-1">
            <Label htmlFor="addr-name">Street name</Label>
            <Input
              id="addr-name"
              value={streetName}
              onChange={(e) => setStreetName(e.target.value)}
              placeholder="e.g. Kent"
            />
          </div>
          <div className="space-y-2 sm:col-span-1">
            <Label htmlFor="addr-type">Street type</Label>
            <Input
              id="addr-type"
              value={streetType}
              onChange={(e) => setStreetType(e.target.value)}
              placeholder="Road / Street / Lane"
            />
          </div>
          <div className="space-y-2 sm:col-span-1">
            <Label htmlFor="addr-postcode">Postcode</Label>
            <Input
              id="addr-postcode"
              value={postCode}
              onChange={(e) => setPostCode(e.target.value)}
              placeholder="e.g. 2000"
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="addr-suburb">Suburb</Label>
            <Input
              id="addr-suburb"
              value={suburb}
              onChange={(e) => setSuburb(e.target.value)}
              placeholder="e.g. Sydney"
            />
          </div>
        </div>
      ) : null}

      {productKey === "land_title_by_owner_name" ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="owner-type">Owner type</Label>
            <Select
              value={nameType}
              onValueChange={(v) =>
                setNameType(v === "Company" ? "Company" : "Person")
              }
            >
              <SelectTrigger id="owner-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Person">Person</SelectItem>
                <SelectItem value="Company">Company</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {nameType === "Person" ? (
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="owner-first">First name</Label>
                <Input
                  id="owner-first"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="owner-middle">Middle name</Label>
                <Input
                  id="owner-middle"
                  value={middleName}
                  onChange={(e) => setMiddleName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="owner-last">Last name</Label>
                <Input
                  id="owner-last"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="owner-company">Company name</Label>
              <Input
                id="owner-company"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder={job?.entityName ?? "Company name"}
              />
            </div>
          )}
        </div>
      ) : null}

      {productKey === "asic_company_extract_current_historical" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="asic-acn">ACN / ABN</Label>
            <Input
              id="asic-acn"
              value={acn}
              onChange={(e) => setAcn(e.target.value)}
              placeholder="9-digit ACN or 11-digit ABN"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="asic-info-type">Information type</Label>
            <Select
              value={asicInfoType}
              onValueChange={(v) =>
                setAsicInfoType(
                  v === "Current" || v === "Relational" ? v : "Historical",
                )
              }
            >
              <SelectTrigger id="asic-info-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Current">Current only</SelectItem>
                <SelectItem value="Historical">Current + historical</SelectItem>
                <SelectItem value="Relational">Relational</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="asic-on-file">On-file</Label>
            <Select
              value={asicOnFile}
              onValueChange={(v) =>
                setAsicOnFile(
                  v === "Preferred" || v === "Required" ? v : "Disabled",
                )
              }
            >
              <SelectTrigger id="asic-on-file">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Disabled">Live data</SelectItem>
                <SelectItem value="Preferred">
                  Prefer cached (cheaper)
                </SelectItem>
                <SelectItem value="Required">
                  Require cached (no live)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      ) : null}

      {productKey === "ppsr_grantor_search" ? (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ppsr-mode">Identify by</Label>
            <Select
              value={ppsrOrgIdMode}
              onValueChange={(v) =>
                setPpsrOrgIdMode(v === "name" ? "name" : "number")
              }
            >
              <SelectTrigger id="ppsr-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="number">
                  Organisation number (ACN / ABN / ARBN / ARSN)
                </SelectItem>
                <SelectItem value="name">Organisation name</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {ppsrOrgIdMode === "number" ? (
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2 sm:col-span-1">
                <Label htmlFor="ppsr-number-type">Type</Label>
                <Select
                  value={ppsrOrgNumberType}
                  onValueChange={(v) =>
                    setPpsrOrgNumberType(
                      v === "Abn" || v === "Arbn" || v === "Arsn" ? v : "Acn",
                    )
                  }
                >
                  <SelectTrigger id="ppsr-number-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Acn">ACN</SelectItem>
                    <SelectItem value="Abn">ABN</SelectItem>
                    <SelectItem value="Arbn">ARBN</SelectItem>
                    <SelectItem value="Arsn">ARSN</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="ppsr-number">Number</Label>
                <Input
                  id="ppsr-number"
                  value={ppsrOrgNumber}
                  onChange={(e) => setPpsrOrgNumber(e.target.value)}
                  placeholder={job?.acn ?? "e.g. 600 003 138"}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="ppsr-name">Organisation name</Label>
              <Input
                id="ppsr-name"
                value={ppsrOrgName}
                onChange={(e) => setPpsrOrgName(e.target.value)}
                placeholder={job?.entityName ?? "Grantor name"}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="ppsr-report-type">Report type</Label>
            <Select
              value={ppsrReportType}
              onValueChange={(v) =>
                setPpsrReportType(v === "Summary" ? "Summary" : "Detailed")
              }
            >
              <SelectTrigger id="ppsr-report-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Detailed">
                  Detailed — includes secured party ACN and address
                </SelectItem>
                <SelectItem value="Summary">Summary</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      ) : null}

      {productKey === "ppsr_grantor_individual_search" ? (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2 sm:col-span-1">
              <Label htmlFor="ppsr-ind-given">Given names</Label>
              <Input
                id="ppsr-ind-given"
                value={ppsrGivenNames}
                onChange={(e) => setPpsrGivenNames(e.target.value)}
                placeholder="John Henry"
              />
            </div>
            <div className="space-y-2 sm:col-span-1">
              <Label htmlFor="ppsr-ind-family">Family name</Label>
              <Input
                id="ppsr-ind-family"
                value={ppsrFamilyName}
                onChange={(e) => setPpsrFamilyName(e.target.value)}
                placeholder="Smith"
              />
            </div>
            <div className="space-y-2 sm:col-span-1">
              <Label htmlFor="ppsr-ind-dob">Date of birth</Label>
              <DatePicker
                value={ppsrDob}
                onChange={(v) => setPpsrDob(v)}
                placeholder="Pick a date"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="ppsr-ind-report-type">Report type</Label>
            <Select
              value={ppsrReportType}
              onValueChange={(v) =>
                setPpsrReportType(v === "Summary" ? "Summary" : "Detailed")
              }
            >
              <SelectTrigger id="ppsr-ind-report-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Detailed">
                  Detailed — includes secured party ACN and address
                </SelectItem>
                <SelectItem value="Summary">Summary</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      ) : null}

      {productKey === "asic_personal_extract_current_historical" ? (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            ASIC personal-name extracts use a two-step flow: lookup the
            individual first, then choose which record(s) to extract. InfoTrack
            bills your firm directly for each extract.
          </p>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2 sm:col-span-1">
              <Label htmlFor="asic-pers-given">Given names</Label>
              <Input
                id="asic-pers-given"
                value={asicPersGiven}
                onChange={(e) => setAsicPersGiven(e.target.value)}
                placeholder="John James"
              />
            </div>
            <div className="space-y-2 sm:col-span-1">
              <Label htmlFor="asic-pers-family">Family name</Label>
              <Input
                id="asic-pers-family"
                value={asicPersFamily}
                onChange={(e) => setAsicPersFamily(e.target.value)}
                placeholder="Smith"
              />
            </div>
            <div className="space-y-2 sm:col-span-1">
              <Label htmlFor="asic-pers-dob">Date of birth (optional)</Label>
              <DatePicker
                value={asicPersDob}
                onChange={(v) => setAsicPersDob(v)}
                placeholder="Narrows lookup"
              />
            </div>
          </div>

          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={
              !asicPersFamily.trim() ||
              !asicPersGiven.trim() ||
              lookupAsicPersonalName.isPending
            }
            onClick={() => lookupAsicPersonalName.mutate()}
          >
            {lookupAsicPersonalName.isPending ? (
              <>
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                Looking up…
              </>
            ) : asicPersParentOrderId ? (
              "Search again"
            ) : (
              "Lookup ASIC records"
            )}
          </Button>

          {asicPersCandidates.length > 0 ? (
            <div className="space-y-3 rounded-md border bg-muted/30 p-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm">
                  Candidates ({asicPersCandidates.length})
                </Label>
                <span className="text-xs text-muted-foreground">
                  {asicPersSelectedIds.size} selected
                </span>
              </div>

              <div className="space-y-2">
                {asicPersCandidates.map((c) => {
                  const checked = asicPersSelectedIds.has(c.nameIdentifier);
                  const supportsHistorical = c.availableExtractTypes.includes(
                    "CurrentAndHistorical",
                  );
                  return (
                    <label
                      key={c.nameIdentifier}
                      htmlFor={`asic-pers-${c.nameIdentifier}`}
                      className="flex cursor-pointer items-start gap-2 rounded border bg-background p-2 text-sm hover:bg-accent/40"
                    >
                      <Checkbox
                        id={`asic-pers-${c.nameIdentifier}`}
                        checked={checked}
                        onCheckedChange={(v) => {
                          const next = new Set(asicPersSelectedIds);
                          if (v) next.add(c.nameIdentifier);
                          else next.delete(c.nameIdentifier);
                          setAsicPersSelectedIds(next);
                        }}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{c.displayLabel}</div>
                        <div className="text-xs text-muted-foreground">
                          ID: {c.nameIdentifier}
                          {supportsHistorical
                            ? null
                            : " · Current extract only"}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="space-y-2">
                <Label htmlFor="asic-pers-extract-type">Extract type</Label>
                <Select
                  value={asicPersExtractType}
                  onValueChange={(v) =>
                    setAsicPersExtractType(
                      v === "Current" ? "Current" : "Historical",
                    )
                  }
                >
                  <SelectTrigger id="asic-pers-extract-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Current">Current only</SelectItem>
                    <SelectItem value="Historical">
                      Current + historical
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {productKey === "ppsr_company_collateral_report" ? (
        <div className="space-y-3 rounded-md border bg-muted/30 p-3">
          <p className="text-xs text-muted-foreground">
            Bundles one or more completed PPSR grantor searches into a single
            consolidated collateral PDF. Pick the searches that should feed the
            report.
          </p>

          {collateralCandidatesQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading completed PPSR searches…
            </div>
          ) : (collateralCandidatesQuery.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No completed PPSR grantor searches on this job yet — run a PPSR
              organisation or individual search first.
            </p>
          ) : (
            <div className="space-y-2">
              {(collateralCandidatesQuery.data ?? []).map((c) => {
                const checked = collateralSelectedIds.has(c._id);
                return (
                  <label
                    key={c._id}
                    htmlFor={`coll-${c._id}`}
                    className="flex cursor-pointer items-start gap-2 rounded border bg-background p-2 text-sm hover:bg-accent/40"
                  >
                    <Checkbox
                      id={`coll-${c._id}`}
                      checked={checked}
                      onCheckedChange={(v) => {
                        const next = new Set(collateralSelectedIds);
                        if (v) next.add(c._id);
                        else next.delete(c._id);
                        setCollateralSelectedIds(next);
                      }}
                    />
                    <div className="flex-1">
                      <div className="font-medium">{c.displayName}</div>
                      <div className="text-xs text-muted-foreground">
                        InfoTrack order #{c.remoteOrderId}
                        {c.completedAt
                          ? ` · completed ${new Date(c.completedAt).toLocaleDateString("en-AU")}`
                          : ""}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      <Button
        type="button"
        disabled={!isReady()}
        onClick={() => commitMutation.mutate()}
      >
        Submit manual order
      </Button>
    </div>
  );
}
